import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useWebPost from "../../utils/useWebPost";
import ImgUploader from "../../components/ImgUploader";
import { Button, Table, Form, Input, message, Space, Popconfirm, Pagination } from "antd";
import renderFileList from "../../components/renderFileList";
import { checkp, getobjkeys } from "../../utils/functions";
import CustModal from "../../components/ShowModal";
import { PlusOutlined } from "@ant-design/icons";
import FullHeightLayout from "../../components/FullHeightLayout";
import DateInput from "../../components/DateInput";

const { TextArea } = Input;

function EquipmentArchives() {
    const { type } = useParams();
    const webPost = useWebPost();
    const [data, setData] = useState([]);
    const [kw, setKw] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(1);
    const [loading, setLoading] = useState(true);
    const [currentItem, setCurrentItem] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [10, 20, 50, 100, 500];
    const [form] = Form.useForm();
    const loadingRef = useRef(false);

    // 表格列配置
    const columns = [
        { title: '日期', dataIndex: 'record_date', width: 120 },
        { title: '设备编号', dataIndex: 'equipment_no', width: 140 },
        { title: '设备名称', dataIndex: 'equipment_name', width: 160 },
        { title: '存档资料记录卡', dataIndex: 'archive_card', render: (v) => renderFileList(v) },
        { title: '设备主要技术性能记录卡', dataIndex: 'tech_performance_card', render: (v) => renderFileList(v) },
        { title: '设备开箱验收记录卡', dataIndex: 'acceptance_card', render: (v) => renderFileList(v) },
        { title: '产品使用说明', dataIndex: 'user_manual', render: (v) => renderFileList(v) },
        { title: '产品合格证书', dataIndex: 'certificate', render: (v) => renderFileList(v) },
        { title: '检定证书', dataIndex: 'verification_certificate', render: (v) => renderFileList(v) },
        { title: '其它', dataIndex: 'other_files', render: (v) => renderFileList(v) },
        { title: '备注', dataIndex: 'remark' },
        checkp('tszm') && {
            title: '操作',
            width: 220,
            render: (_, record) => (
                <Space>
                    <Button type="link" onClick={() => handleEdit(record)}>修改</Button>
                    <Popconfirm
                        title="确定删除该档案吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button type="link" danger>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ].filter(Boolean);

    // 获取列表
    useEffect(() => {
        fetchList();
    }, []);

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const res = await webPost('getEquipmentArchives', { kw, type, page, limit });
            if (res && res.data) {
                setData(res.data || []);
                setTotal(res.total || 0);
            }
        } catch (err) {
            setData([]);
            setTotal(0);
            message.error('数据加载失败');
        } finally {
            setLoading(false);
        }
    };

    // 新增
    const handleAdd = () => {
        setCurrentItem(null);
        form.resetFields();
        setModalVisible(true);
    };

    // 修改
    const handleEdit = (record) => {
        setCurrentItem(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    // 删除
    const handleDelete = async (id) => {
        try {
            const res = await webPost('deleteEquipmentArchives', { id });
            if (res) {
                message.success('删除成功');
                fetchList();
            }
        } catch (err) {
            message.error('删除失败');
        }
    };

    // 提交保存
    const handleSubmit = async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;

        try {
            const values = await form.validateFields();
            // console.log(values, getobjkeys(values));
            const params = { ...values, type };

            if (currentItem) params.id = currentItem.id;

            const res = await webPost('saveEquipmentArchives', params);
            if (res) {
                message.success(currentItem ? '修改成功' : '新增成功');
                setModalVisible(false);
                fetchList();
            }
        } catch (err) {
            message.error('保存失败：' + err.message);
        } finally {
            loadingRef.current = false;
        }
    };

    const onPageChange = (page, size) => {
        setPage(page);
        if (size !== pageSize) setPageSize(size);
        fetchList(page, size);
    };
    return (
        <FullHeightLayout
            headerContent={<Space>
                <Input
                    placeholder="搜索设备编号、名称"
                    onChange={(e) => setKw(e.target.value)}
                    style={{ width: 260 }}
                />
                <Button type="primary" onClick={fetchList} >搜索</Button>
                <Button
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    style={{ position: "absolute", zIndex: 10, right: 16, top: 8 }}
                >
                    新增档案
                </Button>
            </Space>}
            bodyContent={null}
            footerContent={<div style={{
                display: 'flex', justifyContent: 'flex-end',
                alignItems: 'center',
            }}>
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={total}
                    showSizeChanger
                    showQuickJumper
                    showTotal={(_total, range) => `第 ${range[0]}-${range[1]} 条，共 ${_total} 条`}
                    onChange={onPageChange}
                    pageSizeOptions={pageSizeOptions}
                />
            </div>}
        >
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 2000, y: 'calc(100vh - 200px)' }}
            />

            {/* 编辑弹窗 */}
            <CustModal
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                title={currentItem ? '修改设备档案' : '新增设备档案'}
                width={800}
            >
                <Form form={form} layout="vertical" labelAlign="left">
                    <div style={{ display: 'flex', gap: 16 }}>
                        <Form.Item label="日期" name="record_date" rules={[{ required: true }]} style={{ flex: 1 }}>
                            <DateInput />
                        </Form.Item>
                        <Form.Item label="设备编号" name="equipment_no" rules={[{ required: true }]} style={{ flex: 1 }}>
                            <Input  />
                        </Form.Item>
                        <Form.Item label="设备名称" name="equipment_name" rules={[{ required: true }]} style={{ flex: 1 }}>
                            <Input />
                        </Form.Item>
                    </div>

                    <Form.Item label="存档资料记录卡" name="archive_card"><ImgUploader /></Form.Item>
                    <Form.Item label="设备主要技术性能记录卡" name="tech_performance_card"><ImgUploader /></Form.Item>
                    <Form.Item label="设备开箱验收记录卡" name="acceptance_card"><ImgUploader /></Form.Item>
                    <Form.Item label="产品使用说明" name="user_manual"><ImgUploader /></Form.Item>
                    <Form.Item label="产品合格证书" name="certificate"><ImgUploader /></Form.Item>
                    <Form.Item label="检定证书" name="verification_certificate"><ImgUploader /></Form.Item>
                    <Form.Item label="其它" name="other_files"><ImgUploader /></Form.Item>

                    <Form.Item label="备注说明" name="remark">
                        <TextArea rows={3} />
                    </Form.Item>
                </Form>
            </CustModal>
        </FullHeightLayout>
    );
}

export default EquipmentArchives;